import { CategoryBadge } from '@components/CategoryBadge';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { placeHolderData } from '../../config';

interface Contributor {
  id: string;
  givenName: string;
  thumbnailUrl?: string;
}

interface WisdomCardProps {
  album: any;
  contributor?: Contributor;
  albumArtUrl?: string;
  albumName?: string;
  likes?: number;
  isLiked?: boolean;
  onClick?: (e?: React.MouseEvent) => void;
  onFavoriteClick?: (e: React.MouseEvent) => void;
  showLikesCount?: boolean;
  compactSize?: boolean;
}

export const WisdomCard = memo(
  ({
    album,
    contributor,
    albumArtUrl,
    albumName,
    likes,
    isLiked,
    onClick,
    onFavoriteClick,
    showLikesCount = true,
    compactSize = false,
  }: WisdomCardProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const displayAlbumArtUrl = albumArtUrl || placeHolderData.albumCover;
    const displayAlbumName = albumName || album?.name || t('common.unknownAlbum');
    const displayContributorName = contributor?.givenName || t('common.unknownArtist');

    const handleContributorClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (contributor?.id) {
        navigate(`/contributor/${contributor.id}`);
      }
    };

    return (
      <div
        className={`w-full snap-start relative rounded-[16px] overflow-hidden ${
          compactSize ? 'h-[395px]' : 'h-[520px]'
        }`}
        style={{
          backgroundImage: `url(${displayAlbumArtUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}>
        <div
          className="w-full h-full relative rounded-[16px] shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] cursor-pointer hover:shadow-[0_4px_20px_0_rgba(0,0,0,0.50)] transition-all duration-300 opacity-100"
          style={{
            background:
              'linear-gradient(rgb(255 255 255 / 20%) 0%, rgb(0 0 0 / 80%) 100%)',
            backdropFilter: 'blur(50px)',
          }}
          onClick={() => onClick?.()}>
          {/* Audiobooks Badge */}
          <CategoryBadge category="audiobooks" />

          {/* Center Image Section */}
          <div
            className={`absolute inset-0 flex justify-center z-[50] pointer-events-none ${
              compactSize ? 'top-[73px]' : 'top-[97px]'
            }`}>
            <div
              className={`rounded-[8px] overflow-hidden shadow-lg ${
                compactSize ? 'w-[141px] h-[209px]' : 'w-[185px] h-[275.1px]'
              }`}>
              <img
                src={displayAlbumArtUrl}
                alt={displayAlbumName}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Bottom Section */}
          <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-[7px] w-full text-left">
            <div className="text-white line-clamp-2 font-[DM_Serif_Display] font-normal text-[20px] md:text-[24px] leading-[35px]">
              {displayAlbumName}
            </div>

            <div className="flex items-center justify-between gap-3">
              <div
                className="flex items-center gap-[16px] flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleContributorClick}>
                <img
                  src={
                    contributor?.thumbnailUrl ||
                    placeHolderData.contributorThumbnail
                  }
                  alt={displayContributorName}
                  className="w-[56px] h-[56px] rounded-full flex-shrink-0 object-cover"
                />
                <span className="text-white font-[DM_Sans] font-medium text-[14px] md:text-[16px] leading-[16px]">
                  {displayContributorName}
                </span>
              </div>
              {showLikesCount && likes !== undefined && onFavoriteClick && (
                <div
                  className="flex items-center gap-1 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={onFavoriteClick}>
                  {/* <span className="text-white/80 text-[12px] font-[400] leading-[130%]">
                  {likes}
                </span> */}
                  {isLiked ? (
                    <Likedicon width={20} height={20} />
                  ) : (
                    <FavoriteIcon width={20} height={20} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

WisdomCard.displayName = 'WisdomCard';
